import { create } from 'zustand';

/**
 * Theme preference. Dark is the default because the product is a developer
 * tool; the choice is persisted and re-applied before first paint by a small
 * inline script in index.html.
 */

export type ThemePreference = 'dark' | 'light' | 'system';

const STORAGE_KEY = 'design-md-theme';

function readStored(): ThemePreference {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'dark' || value === 'light' || value === 'system') return value;
  } catch {
    // Private mode or storage disabled: fall back to the default.
  }
  return 'dark';
}

function prefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? true;
}

function apply(preference: ThemePreference): boolean {
  const dark = preference === 'dark' || (preference === 'system' && prefersDark());
  document.documentElement.classList.toggle('dark', dark);
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
  return dark;
}

interface ThemeState {
  preference: ThemePreference;
  isDark: boolean;
  setPreference: (preference: ThemePreference) => void;
  /** Cycles dark -> light -> system, for the toolbar toggle. */
  cycle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: 'dark',
  isDark: true,
  setPreference: (preference) => {
    try {
      localStorage.setItem(STORAGE_KEY, preference);
    } catch {
      // Persisting is best-effort.
    }
    set({ preference, isDark: apply(preference) });
  },
  cycle: () => {
    const order: ThemePreference[] = ['dark', 'light', 'system'];
    const next = order[(order.indexOf(get().preference) + 1) % order.length]!;
    get().setPreference(next);
  },
}));

/** Called once at startup to sync the store with what the page already shows. */
export function initializeTheme(): void {
  const preference = readStored();
  useThemeStore.setState({ preference, isDark: apply(preference) });

  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
    if (useThemeStore.getState().preference === 'system') {
      useThemeStore.setState({ isDark: apply('system') });
    }
  });
}
